import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface StrategyPerformanceData {
  strategyType: string;
  totalProfit: number;
  avgReturn: number;
  activeGroups: number;
  costBasis: number;
}

interface StrategyComparisonChartProps {
  data: StrategyPerformanceData[];
}

const StrategyComparisonChart: React.FC<StrategyComparisonChartProps> = ({ data }) => {
  const COLORS: { [key: string]: string } = {
    'REGULAR_LADDER': '#6366f1',
    'FUTURE_LADDER': '#8b5cf6',
    'MOMENTUM_SWING': '#10b981',
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatStrategyName = (name: string) => {
    return name.split('_').map(word =>
      word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const roi = data.costBasis > 0 ? (data.totalProfit / data.costBasis) * 100 : 0;

      return (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          minWidth: '200px'
        }}>
          <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {formatStrategyName(data.strategyType)}
          </p>
          <p style={{ color: data.totalProfit >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
            Profit: {formatCurrency(data.totalProfit)}
          </p>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            ROI: {roi.toFixed(2)}%
          </p>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Avg Return: {(data.avgReturn * 100).toFixed(2)}%
          </p>
          <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
            Active Groups: {data.activeGroups}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 50 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="strategyType"
          tick={{ fontSize: 12 }}
          tickFormatter={formatStrategyName}
          angle={-15}
          textAnchor="end"
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12 }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="totalProfit" radius={[8, 8, 0, 0]}>
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[entry.strategyType] || '#64748b'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StrategyComparisonChart;

